// src/components/games/crossroads/DeckBuilder.tsx
// Deck builder UI for Crossroads TCG

import React, { useState, useMemo } from 'react';
import {
  IonModal,
  IonButton,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonList,
  IonItem,
  IonLabel,
  IonBadge,
  IonSegment,
  IonSegmentButton,
  IonSearchbar,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonIcon,
  IonText,
  IonChip,
} from '@ionic/react';
import {
  add,
  remove,
  checkmarkCircle,
  closeCircle,
} from 'ionicons/icons';

import {
  DeckDefinition,
  CardType,
  ElementType,
  Card,
  CreatureCard,
  ArtCard,
  ManaSourceCard,
  CommanderCard,
} from './types';
import { ALL_CARDS } from './cardData';
import { CardDetailModal } from './CardDetailModal';

interface DeckBuilderProps {
  isOpen: boolean;
  onClose: () => void;
  onDeckComplete: (deck: DeckDefinition) => void;
  initialDeck?: DeckDefinition;
}

const DECK_MIN_CARDS = 40;
const DECK_MAX_CARDS = 60;
const MAX_COPIES_PER_CARD = 3;

export const DeckBuilder: React.FC<DeckBuilderProps> = ({
  isOpen,
  onClose,
  onDeckComplete,
  initialDeck,
}) => {
  const [selectedCommanderId, setSelectedCommanderId] = useState<string | null>(
    initialDeck?.commanderId || null
  );
  const [deckCards, setDeckCards] = useState<Record<string, number>>(
    initialDeck?.cards.reduce((acc, { cardId, count }) => {
      acc[cardId] = count;
      return acc;
    }, {} as Record<string, number>) || {}
  );
  const [filterType, setFilterType] = useState<CardType | 'all'>('all');
  const [searchText, setSearchText] = useState('');
  const [cardDetailModalOpen, setCardDetailModalOpen] = useState(false);
  const [cardDetailId, setCardDetailId] = useState<string | null>(null);

  // Get selected commander
  const selectedCommander = useMemo(() => {
    if (!selectedCommanderId) return null;
    return Object.values(ALL_CARDS).find(
      (card) => card.id === selectedCommanderId && card.type === CardType.COMMANDER
    ) as CommanderCard | null;
  }, [selectedCommanderId]);

  // Get deck's primary element type
  const deckElement = selectedCommander?.elementType || null;

  // Get all available commanders
  const commanders = useMemo(() => {
    return Object.values(ALL_CARDS).filter(
      (card) => card.type === CardType.COMMANDER
    ) as CommanderCard[];
  }, []);

  // Get available cards for deck building (filtered by element and card type)
  const availableCards = useMemo(() => {
    if (!deckElement) return [];

    return Object.values(ALL_CARDS).filter((card) => {
      // Exclude commanders
      if (card.type === CardType.COMMANDER) return false;
      if (card.type === CardType.ENVIRONMENT) return false;

      // Filter by card type
      if (filterType !== 'all' && card.type !== filterType) return false;

      // Filter by search text
      if (searchText && !card.name.toLowerCase().includes(searchText.toLowerCase())) {
        return false;
      }

      // Check element matching
      const cardElement = (card as CreatureCard | ManaSourceCard).elementType;
      if (cardElement && cardElement !== deckElement) return false;

      return true;
    });
  }, [deckElement, filterType, searchText]);

  // Calculate deck statistics
  const totalCards = Object.values(deckCards).reduce((sum, count) => sum + count, 0);
  const isValidDeck = totalCards >= DECK_MIN_CARDS && totalCards <= DECK_MAX_CARDS && selectedCommanderId;

  // Add card to deck
  const addCard = (cardId: string) => {
    const currentCount = deckCards[cardId] || 0;
    if (currentCount < MAX_COPIES_PER_CARD) {
      setDeckCards((prev) => ({
        ...prev,
        [cardId]: currentCount + 1,
      }));
    }
  };

  // Remove card from deck
  const removeCard = (cardId: string) => {
    const currentCount = deckCards[cardId] || 0;
    if (currentCount > 0) {
      if (currentCount === 1) {
        setDeckCards((prev) => {
          const newDeck = { ...prev };
          delete newDeck[cardId];
          return newDeck;
        });
      } else {
        setDeckCards((prev) => ({
          ...prev,
          [cardId]: currentCount - 1,
        }));
      }
    }
  };

  // Complete deck building
  const completeDeck = () => {
    if (!isValidDeck || !selectedCommanderId || !deckElement) return;

    const deck: DeckDefinition = {
      name: `${selectedCommander?.name}'s Deck`,
      elementType: deckElement,
      commanderId: selectedCommanderId,
      cards: Object.entries(deckCards).map(([cardId, count]) => ({
        cardId,
        count,
      })),
    };

    onDeckComplete(deck);
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Deck Builder</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>Close</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {/* Commander Selection */}
        {!selectedCommanderId ? (
          <div style={{ padding: '16px' }}>
            <h2>Select Your Commander</h2>
            <IonList>
              {commanders.map((commander) => (
                <IonCard
                  key={commander.id}
                  style={{ cursor: 'pointer' }}
                >
                  <div onClick={() => setSelectedCommanderId(commander.id)}>
                    <IonCardHeader>
                      <IonCardTitle>{commander.name}</IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                      <IonBadge color="primary">{commander.elementType}</IonBadge>
                      <IonBadge color="secondary">{commander.health} HP</IonBadge>
                      <p style={{ marginTop: '8px' }}>{commander.flavorText}</p>
                    </IonCardContent>
                  </div>
                  <IonCardContent>
                    <IonButton
                      size="small"
                      fill="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCardDetailId(commander.id);
                        setCardDetailModalOpen(true);
                      }}
                    >
                      View Details
                    </IonButton>
                  </IonCardContent>
                </IonCard>
              ))}
            </IonList>
          </div>
        ) : (
          /* Deck Building View */
          <div style={{ padding: '16px' }}>
            {/* Commander Display */}
            <IonCard>
              <IonCardHeader>
                <IonCardTitle>
                  Commander: {selectedCommander?.name}
                  <IonButton
                    size="small"
                    fill="clear"
                    onClick={() => {
                      setSelectedCommanderId(null);
                      setDeckCards({});
                    }}
                  >
                    Change
                  </IonButton>
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <IonBadge color="primary">{deckElement}</IonBadge>
                <IonText>
                  <p>
                    Deck: {totalCards} / {DECK_MAX_CARDS} cards
                    {isValidDeck ? (
                      <IonIcon icon={checkmarkCircle} color="success" style={{ marginLeft: '8px' }} />
                    ) : (
                      <IonIcon icon={closeCircle} color="danger" style={{ marginLeft: '8px' }} />
                    )}
                  </p>
                  <p>
                    {totalCards < DECK_MIN_CARDS && `Need ${DECK_MIN_CARDS - totalCards} more cards`}
                  </p>
                </IonText>
              </IonCardContent>
            </IonCard>

            {/* Card Type Filter */}
            <IonSegment value={filterType} onIonChange={(e) => setFilterType(e.detail.value as CardType | 'all')}>
              <IonSegmentButton value="all">
                <IonLabel>All</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value={CardType.CREATURE}>
                <IonLabel>Creatures</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value={CardType.ART}>
                <IonLabel>Spells</IonLabel>
              </IonSegmentButton>
              <IonSegmentButton value={CardType.MANA_SOURCE}>
                <IonLabel>Mana</IonLabel>
              </IonSegmentButton>
            </IonSegment>

            {/* Search Bar */}
            <IonSearchbar
              value={searchText}
              onIonInput={(e) => setSearchText(e.detail.value || '')}
              placeholder="Search cards..."
            />

            {/* Available Cards */}
            <h3>Available Cards</h3>
            <IonList>
              {availableCards.map((card) => {
                const inDeck = deckCards[card.id] || 0;
                const canAdd = inDeck < MAX_COPIES_PER_CARD;

                return (
                  <IonItem key={card.id}>
                    <IonLabel
                      onClick={() => {
                        setCardDetailId(card.id);
                        setCardDetailModalOpen(true);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <h2>{card.name}</h2>
                      <p>
                        {card.type === CardType.CREATURE && `${(card as CreatureCard).power}/${(card as CreatureCard).health}`}
                        {card.type === CardType.ART && 'Art Spell'}
                        {card.type === CardType.MANA_SOURCE && 'Mana Source'}
                      </p>
                    </IonLabel>
                    {inDeck > 0 && (
                      <IonChip color="primary">
                        <IonLabel>{inDeck}x</IonLabel>
                      </IonChip>
                    )}
                    <IonButton
                      slot="end"
                      size="small"
                      onClick={() => removeCard(card.id)}
                      disabled={inDeck === 0}
                    >
                      <IonIcon icon={remove} />
                    </IonButton>
                    <IonButton
                      slot="end"
                      size="small"
                      onClick={() => addCard(card.id)}
                      disabled={!canAdd}
                    >
                      <IonIcon icon={add} />
                    </IonButton>
                  </IonItem>
                );
              })}
            </IonList>

            {/* Complete Button */}
            <IonButton
              expand="block"
              onClick={completeDeck}
              disabled={!isValidDeck}
              style={{ marginTop: '16px' }}
            >
              Complete Deck ({totalCards} cards)
            </IonButton>
          </div>
        )}
      </IonContent>

      {/* Card Detail Modal */}
      <CardDetailModal
        card={cardDetailId ? ALL_CARDS[cardDetailId] : null}
        isOpen={cardDetailModalOpen}
        onDismiss={() => setCardDetailModalOpen(false)}
        showPlayButton={false}
      />
    </IonModal>
  );
};
